"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
}

declare global {
  interface Window {
    __rigodonInstallPrompt?: BeforeInstallPromptEvent;
  }
}

export function InstallButton() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "android" | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
      return;
    }

    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
      setPlatform("ios");
    } else if (/android/.test(userAgent)) {
      setPlatform("android");
    }

    if (/iphone|ipad|ipod|android/.test(userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
      window.setTimeout(() => setShowInvite(true), 700);
    }

    const onInstallReady = () => {
      setDeferred(window.__rigodonInstallPrompt ?? null);
    };

    const onPrompt = (e: Event) => {
      e.preventDefault();
      const prompt = e as BeforeInstallPromptEvent;
      window.__rigodonInstallPrompt = prompt;
      setDeferred(prompt);
    };

    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      window.__rigodonInstallPrompt = undefined;
    };

    onInstallReady();
    window.addEventListener("rigodon-install-ready", onInstallReady);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("rigodon-install-ready", onInstallReady);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = async () => {
    setShowInvite(false);
    if (!deferred) {
      setShowHelp((visible) => !visible);
      return;
    }
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setDeferred(null);
      window.__rigodonInstallPrompt = undefined;
    }
  };

  if (installed) return null;

  return (
    <div className="relative">
      <AnimatePresence>
        {showInvite && platform && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            className="pointer-events-auto fixed inset-x-4 top-20 z-50 w-auto rounded-2xl border border-cyan/40 bg-panel p-4 text-left shadow-[0_0_28px_rgba(0,229,255,0.22)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-10 sm:w-72"
          >
            <button
              type="button"
              onClick={() => setShowInvite(false)}
              aria-label="Cerrar aviso de instalación"
              className="absolute right-3 top-2 text-lg text-text-faint hover:text-text"
            >
              ×
            </button>
            <p className="mb-1 font-mono text-[10px] tracking-[0.16em] text-cyan uppercase">
              GORDO · PWA
            </p>
            <p className="mb-3 text-sm text-text">
              {platform === "ios"
                ? "Añade la app a tu pantalla de inicio para abrirla como una aplicación normal."
                : "Instala la app para abrirla como una aplicación normal."}
            </p>
            <button
              type="button"
              onClick={handleClick}
              className="rounded-full border border-cyan/50 bg-cyan/10 px-4 py-2 font-mono text-[10px] tracking-[0.16em] text-cyan uppercase hover:bg-cyan/20"
            >
              Instalar ahora
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
      {(deferred || platform) && (
        <motion.button
          key="install-btn"
          type="button"
          onClick={handleClick}
          aria-label="Instalar GORDO"
          title={deferred ? "Instalar GORDO" : "Cómo instalar GORDO"}
          initial={{ opacity: 0, scale: 0.7, y: 4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.7, y: 4 }}
          whileHover={{
            scale: 1.08,
            borderColor: "rgba(255,176,32,0.55)",
          }}
          whileTap={{ scale: 0.92 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border border-line bg-panel/60 text-text-dim backdrop-blur-sm transition-[color,box-shadow] hover:text-amber hover:shadow-[0_0_16px_rgba(255,176,32,0.35)]"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3v12" />
            <path d="m7 10 5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
        </motion.button>
      )}
      </AnimatePresence>
      <AnimatePresence>
        {showHelp && !deferred && platform && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="pointer-events-auto fixed inset-x-4 top-20 z-50 w-auto rounded-xl border border-line bg-panel p-3 text-left text-xs leading-relaxed text-text-dim shadow-[0_0_24px_rgba(0,229,255,0.18)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-10 sm:w-64"
          >
            {platform === "ios" ? (
              <>
                <p className="mb-1 font-mono text-[10px] tracking-[0.16em] text-cyan uppercase">
                  Añadir a inicio
                </p>
                En Safari pulsa <strong className="text-text">Compartir</strong> y después <strong className="text-text">Añadir a pantalla de inicio</strong>.
              </>
            ) : (
              <>
                <p className="mb-1 font-mono text-[10px] tracking-[0.16em] text-cyan uppercase">
                  Instalar aplicación
                </p>
                En Chrome abre el menú <strong className="text-text">⋮</strong> y pulsa <strong className="text-text">Instalar aplicación</strong> o <strong className="text-text">Añadir a pantalla de inicio</strong>.
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
